import { API_BASE_URL, API_KEY } from '../config';
import {
  ApiErrorBody,
  DocTypesResponse,
  DocTypeInfo,
  OcrOptions,
  OcrResponse,
} from '../types';

/** Error carrying the server's machine-readable code + HTTP status. */
export class OcrApiError extends Error {
  code: string;
  status: number;
  details?: unknown;

  constructor(message: string, code: string, status: number, details?: unknown) {
    super(message);
    this.name = 'OcrApiError';
    this.code = code;
    this.status = status;
    this.details = details;
  }
}

function authHeaders(): Record<string, string> {
  // Send the key only in dev; in production the Catalyst session cookie auths.
  return API_KEY ? { 'x-api-key': API_KEY } : {};
}

async function parseJson<T>(res: Response): Promise<T> {
  const text = await res.text();
  let body: unknown;
  try {
    body = text ? JSON.parse(text) : {};
  } catch {
    throw new OcrApiError(
      `Unexpected non-JSON response (HTTP ${res.status}).`,
      'INVALID_RESPONSE',
      res.status
    );
  }

  if (!res.ok || (body as ApiErrorBody).success === false) {
    const err = (body as ApiErrorBody).error;
    throw new OcrApiError(
      err?.message || `Request failed (HTTP ${res.status}).`,
      err?.code || 'REQUEST_FAILED',
      res.status,
      err?.details
    );
  }
  return body as T;
}

/** Fetch the catalogue of built-in document types. */
export async function getDocTypes(): Promise<DocTypeInfo[]> {
  const res = await fetch(`${API_BASE_URL}/doc-types`, {
    method: 'GET',
    credentials: 'include',
    headers: { ...authHeaders() },
  });
  const body = await parseJson<DocTypesResponse>(res);
  return body.docTypes;
}

/** Upload one or more files for OCR extraction. */
export async function postOcr(files: File[], options: OcrOptions = {}): Promise<OcrResponse> {
  const form = new FormData();
  // Field name "files" matches what Zoho Deluge sends; the server accepts any.
  files.forEach((file) => form.append('files', file));
  if (options.docType) form.append('docType', options.docType);
  if (options.prompt) form.append('prompt', options.prompt);
  if (options.schema) form.append('schema', JSON.stringify(options.schema));

  const res = await fetch(`${API_BASE_URL}/ocr`, {
    method: 'POST',
    credentials: 'include',
    headers: { ...authHeaders() }, // do NOT set Content-Type; the browser sets the multipart boundary
    body: form,
  });
  return parseJson<OcrResponse>(res);
}
